import React from "react";

/**
 * Minimal line-art SVG illustrations for empty states.
 * Palette: #94a3b8 (muted), #cbd5e1 (lighter), #e2e8f0 (lightest), #2563eb (accent blue).
 */

/** Document with a signature line and pen */
export const DocumentsIllustration: React.FC = () => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Document body */}
    <rect
      x="28"
      y="14"
      width="58"
      height="76"
      rx="6"
      fill="#e2e8f0"
      stroke="#cbd5e1"
      strokeWidth="1.5"
    />
    {/* Folded corner */}
    <path
      d="M72 14 L86 28 L72 28 Z"
      fill="#cbd5e1"
      stroke="#94a3b8"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    {/* Text lines */}
    <rect x="38" y="36" width="32" height="3" rx="1.5" fill="#cbd5e1" />
    <rect x="38" y="44" width="26" height="3" rx="1.5" fill="#cbd5e1" />
    <rect x="38" y="52" width="30" height="3" rx="1.5" fill="#cbd5e1" />
    <rect x="38" y="60" width="20" height="3" rx="1.5" fill="#cbd5e1" />
    {/* Signature line */}
    <line
      x1="38"
      y1="76"
      x2="74"
      y2="76"
      stroke="#94a3b8"
      strokeWidth="1.5"
      strokeDasharray="3 2"
    />
    {/* Signature scribble */}
    <path
      d="M42 74 C44 70, 48 72, 50 74 C52 76, 54 70, 58 72 C60 73, 62 71, 66 73"
      stroke="#2563eb"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
    {/* Pen */}
    <g transform="translate(72, 82) rotate(-35)">
      <rect x="0" y="0" width="4" height="28" rx="1.5" fill="#94a3b8" />
      <polygon points="0,28 4,28 2,34" fill="#94a3b8" />
      <rect x="0" y="0" width="4" height="6" rx="1" fill="#2563eb" />
    </g>
  </svg>
);

/** Stacked document templates with a plus icon */
export const TemplatesIllustration: React.FC = () => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Back document */}
    <rect
      x="36"
      y="12"
      width="52"
      height="66"
      rx="6"
      fill="#e2e8f0"
      stroke="#cbd5e1"
      strokeWidth="1.2"
    />
    {/* Middle document */}
    <rect
      x="30"
      y="20"
      width="52"
      height="66"
      rx="6"
      fill="#f1f5f9"
      stroke="#cbd5e1"
      strokeWidth="1.2"
    />
    {/* Front document */}
    <rect
      x="24"
      y="28"
      width="52"
      height="66"
      rx="6"
      fill="white"
      stroke="#94a3b8"
      strokeWidth="1.5"
    />
    {/* Text lines on front doc */}
    <rect x="34" y="40" width="28" height="3" rx="1.5" fill="#cbd5e1" />
    <rect x="34" y="48" width="22" height="3" rx="1.5" fill="#e2e8f0" />
    <rect x="34" y="56" width="26" height="3" rx="1.5" fill="#e2e8f0" />
    <rect x="34" y="64" width="18" height="3" rx="1.5" fill="#e2e8f0" />
    <rect x="34" y="72" width="30" height="3" rx="1.5" fill="#e2e8f0" />
    {/* Plus circle */}
    <circle cx="88" cy="88" r="16" fill="#2563eb" />
    <line
      x1="88"
      y1="80"
      x2="88"
      y2="96"
      stroke="white"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <line
      x1="80"
      y1="88"
      x2="96"
      y2="88"
      stroke="white"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

/** Envelope with paper airplane */
export const SentIllustration: React.FC = () => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Envelope body */}
    <rect
      x="16"
      y="38"
      width="72"
      height="50"
      rx="6"
      fill="#e2e8f0"
      stroke="#94a3b8"
      strokeWidth="1.5"
    />
    {/* Envelope flap */}
    <path
      d="M16 44 L52 68 L88 44"
      stroke="#94a3b8"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Inner fold lines */}
    <path
      d="M16 88 L42 66"
      stroke="#cbd5e1"
      strokeWidth="1"
      strokeLinecap="round"
    />
    <path
      d="M88 88 L62 66"
      stroke="#cbd5e1"
      strokeWidth="1"
      strokeLinecap="round"
    />
    {/* Paper airplane */}
    <g transform="translate(74, 16) rotate(15)">
      <path
        d="M0 16 L32 8 L8 0 Z"
        fill="#2563eb"
        stroke="#2563eb"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      <path
        d="M0 16 L12 12 L8 0"
        fill="#1d4ed8"
        stroke="#2563eb"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
      {/* Trail lines */}
      <line
        x1="-4"
        y1="18"
        x2="-14"
        y2="22"
        stroke="#93c5fd"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="-2"
        y1="22"
        x2="-10"
        y2="24"
        stroke="#93c5fd"
        strokeWidth="1"
        strokeLinecap="round"
      />
      <line
        x1="-6"
        y1="14"
        x2="-12"
        y2="16"
        stroke="#bfdbfe"
        strokeWidth="1"
        strokeLinecap="round"
      />
    </g>
    {/* Checkmark circle */}
    <circle cx="88" cy="82" r="12" fill="#ecfdf3" stroke="#34d399" strokeWidth="1.5" />
    <path
      d="M83 82 L87 86 L94 78"
      stroke="#166534"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
